// client/src/components/hooks/useAuth.js
import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { getLogger } from '@/utils/logger';
import { AuthStateContext, fallbackAuthState } from '@/components/Auth0/AuthStateContext';

const log = getLogger('useAuth');
const AUTH0_NAMESPACE = 'https://landivo.com';

const decodeJwtPayload = (accessToken) => {
  try {
    const tokenParts = accessToken.split('.');
    if (tokenParts.length !== 3) {
      return null;
    }
    const base64Url = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64Url.padEnd(Math.ceil(base64Url.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch (error) {
    log.error(
      `[useAuth:decodeJwtPayload] > [Request]: parseToken > [Response]: error=${error?.message || "unknown"}`
    );
    return null;
  }
};

const resolveRolesFromUser = (auth0User) => {
  if (!auth0User) {
    return { roles: [], source: 'none' };
  }

  if (Array.isArray(auth0User[`${AUTH0_NAMESPACE}/roles`])) {
    return { roles: auth0User[`${AUTH0_NAMESPACE}/roles`], source: `${AUTH0_NAMESPACE}/roles` };
  }

  if (Array.isArray(auth0User.roles)) {
    return { roles: auth0User.roles, source: 'auth0User.roles' };
  }

  if (Array.isArray(auth0User[AUTH0_NAMESPACE]?.roles)) {
    return { roles: auth0User[AUTH0_NAMESPACE].roles, source: `${AUTH0_NAMESPACE}.roles` };
  }

  return { roles: [], source: 'none' };
};

const resolvePermissionsFromPayload = (payload) => {
  if (!payload) {
    return { permissions: [], source: 'none' };
  }

  if (Array.isArray(payload.permissions)) {
    return { permissions: payload.permissions, source: 'payload.permissions' };
  }

  if (Array.isArray(payload[`${AUTH0_NAMESPACE}/permissions`])) {
    return {
      permissions: payload[`${AUTH0_NAMESPACE}/permissions`],
      source: `${AUTH0_NAMESPACE}/permissions`,
    };
  }

  if (Array.isArray(payload[AUTH0_NAMESPACE]?.permissions)) {
    return {
      permissions: payload[AUTH0_NAMESPACE].permissions,
      source: `${AUTH0_NAMESPACE}.permissions`,
    };
  }

  return { permissions: [], source: 'none' };
};


export function useAuthLegacy() {
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    user: auth0User,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();
  
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsReady, setPermissionsReady] = useState(false);

  const roleResolution = useMemo(() => resolveRolesFromUser(auth0User), [auth0User]);
  const userRoles = roleResolution.roles;

  useEffect(() => {
    if (auth0Loading) {
      setPermissionsReady(false);
    }
  }, [auth0Loading]);

  // Calculate admin/agent status from roles
  const { isAdmin, isAgent } = useMemo(() => {
    return {
      isAdmin: userRoles.includes('Admin'),
      isAgent: userRoles.includes('Agent')
    };
  }, [userRoles]);
  
  // Extract roles from Auth0 identity
  useEffect(() => {
    if (auth0User) {
      log.info(
        `[useAuth:extractRoles] > [Request]: readAuth0User > [Response]: auth0UserLoaded > [Comment]: auth0Id=${auth0User?.sub || "unknown"}`
      );

      if (roleResolution.source !== 'none') {
        log.info(
          `[useAuth:extractRoles] > [Request]: readRoles > [Response]: found > [Comment]: source=${roleResolution.source} roles=${JSON.stringify(roleResolution.roles)}`
        );
      } else {
        log.info("[useAuth:extractRoles] > [Request]: readRoles > [Response]: none");
      }
    } else if (!auth0Loading) {
      log.info("[useAuth:extractRoles] > [Request]: readRoles > [Response]: none");
    }
  }, [auth0User, auth0Loading, roleResolution.roles, roleResolution.source]);

  // Get the token and extract permissions from it
  useEffect(() => {
    let isCancelled = false;

    const fetchTokenAndPermissions = async () => {
      if (auth0Loading) {
        return;
      }

      if (isAuthenticated) {
        setPermissionsReady(false);
        try {
          const accessToken = await getAccessTokenSilently();
          if (isCancelled) return;
          
          const payload = decodeJwtPayload(accessToken);
          const permissionResolution = resolvePermissionsFromPayload(payload);
          if (payload && permissionResolution.source !== 'none') {
            log.info(
              `[useAuth:fetchTokenAndPermissions] > [Request]: parseToken > [Response]: payloadDecoded`
            );
            log.info(
              `[useAuth:fetchTokenAndPermissions] > [Request]: parseToken > [Response]: permissionsFound > [Comment]: source=${permissionResolution.source} permissions=${JSON.stringify(permissionResolution.permissions)}`
            );
          } else {
            log.info(
              "[useAuth:fetchTokenAndPermissions] > [Request]: parseToken > [Response]: no permissions"
            );
          }

          if (!isCancelled) {
            setUserPermissions(permissionResolution.permissions);
          }
          
          if (!isCancelled) {
            setPermissionsReady(true);
          }
          log.info(
            "[useAuth:fetchTokenAndPermissions] > [Request]: getAccessTokenSilently > [Response]: success"
          );
        } catch (error) {
          log.error(
            `[useAuth:fetchTokenAndPermissions] > [Request]: getAccessTokenSilently > [Response]: error=${error?.message || "unknown"}`
          );
          if (!isCancelled) {
            setUserPermissions([]);
            setPermissionsReady(true);
          }
        }
      } else {
        if (!isCancelled) {
          setUserPermissions([]);
          setPermissionsReady(true);
        }
      }
    };
    
    fetchTokenAndPermissions();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, getAccessTokenSilently, auth0Loading]);

  // Function to get auth token for API calls
  const getTokenOrThrow = useCallback(async (options = undefined) => {
    if (!isAuthenticated) {
      throw new Error('Account is not authenticated');
    }
    return options
      ? getAccessTokenSilently(options)
      : getAccessTokenSilently();
  }, [isAuthenticated, getAccessTokenSilently]);

  // Function to get auth token for API calls
  const getToken = useCallback(async (options = undefined) => {
    if (!isAuthenticated) return null;
    
    try {
      // Always use Auth0 SDK so token freshness/refresh rules are applied.
      const newToken = options
        ? await getAccessTokenSilently(options)
        : await getAccessTokenSilently();
      return newToken;
    } catch (error) {
      log.error(
        `[useAuth:getToken] > [Request]: getAccessTokenSilently > [Response]: error=${error?.message || "unknown"}`
      );
      return null;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Combined loading state
  const isLoading = auth0Loading || !permissionsReady;

  return useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      auth0User: auth0User ? { ...auth0User, needsProfileCompletion: false } : null,
      userRoles,
      userPermissions,
      isAdmin,
      isAgent,
      needsProfileCompletion: false,
      loginWithRedirect,
      logout,
      getToken,
      getTokenOrThrow,
    }),
    [
      isAuthenticated,
      isLoading,
      auth0User,
      userRoles,
      userPermissions,
      isAdmin,
      isAgent,
      loginWithRedirect,
      logout,
      getToken,
      getTokenOrThrow,
    ]
  );
}

export function useAuth() {
  const authState = useContext(AuthStateContext);
  if (authState === fallbackAuthState) {
    log.error(
      '[useAuth] > [Response]: AuthStateProvider missing; using fallback auth state'
    );
  }
  return authState;
}
